import classnames from 'classnames';
import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import { Transaction } from '../transactions/transaction/transaction';
import ErrorSubmission from './error-submission';
import {
  appServiceLines,
  cmxDateTime,
  commonEnums
} from '@codametrix/ui-common';
import { ProcessStatus } from '../process-status/process-status';
import { LoadingSlugs } from '@codametrix/ui-components';
import { Icon } from '@codametrix/ui-components';
import InvoiceMetadata from './invoice-metadata/invoice-metadata';

type CodeBenchPanelProps = {
  editable?: boolean;
  showProcessStatus?: boolean;
  benchLoading?: boolean;
  benchEditable?: Accelerate.BenchEditableState;
  caseDetailProps?: any;
  codableInvoices?: CMxCommonApp.CodableInvoice[];
  processInstance?: AmplifyAPI.ProcessInstance;
  onCodeUpdate?: CMxCommonApp.CodeChangeHandler;
  onCodeRemove?: CMxCommonApp.CodeChangeHandler;
  onTimeBasedCodeAdd?: (
    changeDetail: CMxCommonApp.CodeChange,
    time: string,
    age: string
  ) => void;
  onTimeBasedCodeUpdate?: (
    changeDetail: CMxCommonApp.CodeChange,
    time: string,
    age: string
  ) => void;
  onCodeSearchDrop?: CMxCommonApp.CodeChangeHandler;
  onCodeSearch?: CMxCommonApp.CodeSearchHandler;
  onCodeSearchSelection?: CMxCommonApp.CodeSearchSelectionHandler;
  onFeatureSelection?: (selection: CMxCommonApp.ChargeSessionSelection) => void;
  onInvoiceMetaEdit?: (invoice: AmplifyAPI.Invoice) => void;
  onChargeSessionAction?: CMxCommonApp.TransactionActionHandler;
  onTransactionSharedDiagnosisChange?: CMxCommonApp.TransactionSharedCodeHandler;
  handleEnableTechPro: (
    transactionCodeChange: CMxCommonApp.TechCodableTransaction
  ) => void;
  handleDisableTechPro: (
    transactionCodeChange: CMxCommonApp.TechCodableTransaction
  ) => void;
  panelActivations?: CMxCommonApp.PanelActivations;
  searchResults?: {
    [key: string]: CMxCommonApp.CodeInfo[];
  };
  codeEdits: CMxCommonApp.CodeEditInputs;
  customClaimsRelatedCases?: CMxAPI.CaseRun[];
  customClaimsRelatedCasesLoadState?: CMxCommonApp.Loading;
  openChildWindow?: (windowOpenArgs: CMxCommonApp.WindowOpenArguments) => void;
} & CMxCommonApp.ChargeSessionsMeta &
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type CodeBenchPanelState = {
  editState: {
    add: boolean;
  };
};

class CodeBenchPanel extends React.Component<
  CodeBenchPanelProps,
  CodeBenchPanelState
> {
  static displayName = 'CodeBenchPanel';

  static defaultProps = {
    onCodeUpdate: (changeDetail: CMxCommonApp.CodeChange) => {},
    onCodeRemove: (changeDetail: CMxCommonApp.CodeChange) => {},
    onTimeBasedCodeAdd: (
      changeDetail: CMxCommonApp.CodeChange,
      time: string,
      age: string
    ) => {},
    onTimeBasedCodeUpdate: (
      changeDetail: CMxCommonApp.CodeChange,
      time: string,
      age: string
    ) => {},
    onCodeSearchDrop: (changeDetail: CMxCommonApp.CodeChange) => {},
    onCodeSearch: (codeQuery: CMxCommonApp.CodeQuery) => {},
    onCodeSearchSelection: (searchSelection: CMxCommonApp.SearchResult) => {},
    onTransactionSharedDiagnosisChange: (
      changeDetail: CMxCommonApp.TransactionSharedCodeChange
    ) => {},
    onFeatureSelection: (feature: CMxCommonApp.ChargeSessionSelection) => {},
    onInvoiceMetaEdit: (invoice: AmplifyAPI.Invoice) => {},
    onChargeSessionAction: (
      transactionAction: CMxCommonApp.TransactionAction
    ) => {},
    handleEnableTechPro: (
      transactionCodeChange: CMxCommonApp.TechCodableTransaction
    ) => {},
    handleDisableTechPro: (
      transactionCodeChange: CMxCommonApp.TechCodableTransaction
    ) => {},
    codableInvoices: [],
    searchResults: {},
    editable: true,
    panelActivations: {},
    codeEdits: {},
    counts: {
      procedures: 0,
      diagnosis: 0,
      modifiers: 0
    }
  };

  constructor(props: CodeBenchPanelProps) {
    super(props);
    this.state = {
      editState: {
        add: false
      }
    };
  }

  render() {
    const {
      searchResults,
      counts,
      editable,
      benchEditable,
      benchLoading,
      onCodeSearch,
      onCodeSearchSelection,
      onCodeUpdate,
      onCodeRemove,
      onTimeBasedCodeAdd,
      onTimeBasedCodeUpdate,
      onCodeSearchDrop,
      onFeatureSelection,
      onInvoiceMetaEdit: onChargeSessionMetaEdit,
      onChargeSessionAction,
      onTransactionSharedDiagnosisChange,
      panelActivations,
      codableInvoices,
      processInstance,
      codeEdits,
      showProcessStatus,
      caseDetailProps,
      handleEnableTechPro,
      handleDisableTechPro,
      customClaimsRelatedCases,
      customClaimsRelatedCasesLoadState,
      openChildWindow,
      ...rest
    } = this.props;

    const styleClasses = ['code-bench-panel'];

    let counter = 0;
    const invoices = codableInvoices.map((codableInvoice, idx) => {
      const providerName = codableInvoice.billing_provider_name ?? '';
      const performingName = codableInvoice.performing_provider_name ?? '';
      const referringName = codableInvoice.referring_provider_name ?? '';
      const billArea = codableInvoice.fee_schedule ?? 'None';
      const department = codableInvoice?.department?.name ?? '';
      const dateOfService = codableInvoice.date_of_service
        ? cmxDateTime.format(
            codableInvoice.date_of_service,
            cmxDateTime.FORMATS.DATE_OF_SERVICE
          )
        : 'Date not selected';

      let txns;

      txns = codableInvoice.transactions.map(
        (txn: CMxCommonApp.CodableTransaction) => {
          const rawResults = searchResults[txn.guid] ?? [];
          if (codableInvoice.type !== commonEnums.InvoiceTypes.SHARED_CODES) {
            counter++;
          }

          return (
            <Transaction
              key={`txn-${txn.guid}`}
              codeEdits={codeEdits}
              onChargeSessionAction={onChargeSessionAction}
              panelActivations={panelActivations}
              invoice={codableInvoice}
              processInstance={processInstance}
              // focus the user's cursor on the first charge to code.
              autoFocus={idx === 0}
              editable={editable}
              onFeatureSelection={onFeatureSelection}
              searchResults={rawResults}
              onCodeSearch={onCodeSearch}
              onCodeSearchSelection={onCodeSearchSelection}
              handleCodeChange={onCodeUpdate}
              handleCodeRemove={onCodeRemove}
              handleTimeBasedCodeAdd={onTimeBasedCodeAdd}
              handleTimeBasedCodeUpdate={onTimeBasedCodeUpdate}
              handleCodeSearchDrop={onCodeSearchDrop}
              onTransactionSharedDiagnosisChange={
                onTransactionSharedDiagnosisChange
              }
              lineNumber={counter}
              codableTransaction={txn}
              handleEnableTechPro={handleEnableTechPro}
              handleDisableTechPro={handleDisableTechPro}
            />
          );
        }
      );

      const invoiceType =
        codableInvoice?.type?.toLowerCase().replace(/\_/g, '-') ?? 'unknown';
      const invoiceClasses = [
        'codable-invoice',
        'qa-codable-invoice',
        `invoice-${invoiceType}`
      ];

      const isIPCServiceLine = appServiceLines.isIPC(
        processInstance?.serviceLine
      );

      return (
        <div className={classnames(invoiceClasses)} key={`invoice-${idx}`}>
          <div className="invoice-meta">
            <div className="invoice-meta-line invoice-meta-provider">
              {editable && (
                <ErrorSubmission
                  errorMessages={codableInvoice?.errorMessages}
                  messageStyle="inv-error-message"
                  messageType="error"
                />
              )}
              <InvoiceMetadata
                codableInvoice={codableInvoice}
                onInvoiceMetaEdit={this.props.onInvoiceMetaEdit}
                dateOfService={dateOfService}
                department={department}
                performingName={performingName}
                providerName={providerName}
                referringName={referringName}
                editable={editable}
              />
            </div>
          </div>

          {txns}
        </div>
      );
    });

    const noInvoices = (
      <div className="no-transactions">
        <div className="empty-message editable">
          This case does not have any charges. Add a service date and providers
          with the charge button below, then add procedures to the set.
        </div>
      </div>
    );

    return (
      <div {...rest} className={classnames(styleClasses)}>
        {showProcessStatus && (
          <div className="status-section process-status-section">
            <ProcessStatus benchEditable={benchEditable} />
          </div>
        )}

        {customClaimsRelatedCasesLoadState?.loading ? (
          <LoadingSlugs numberItems={5} />
        ) : (
          processInstance?.errorMessages?.length > 0 && (
            <ErrorSubmission
              errorMessages={processInstance.errorMessages}
              messageHeader={
                benchEditable.submitted
                  ? 'We have found the following errors:'
                  : 'We have found the following that reduce the coding quality of this case:'
              }
              customClaimsRelatedProps={{
                cases: customClaimsRelatedCases,
                onOpenRelatedCase: this.props.openChildWindow,
                applyErrorTextStyle: !benchEditable.submitted
              }}
              messageStyle="case-error-message"
              messageType={benchEditable.submitted ? 'warning' : 'error'}
            />
          )
        )}

        {counter === 0 ? noInvoices : invoices}
      </div>
    );
  }
}

export { CodeBenchPanel };
